import * as React from "react";
import { useAppSelector } from "@/store/hooks";
import { selectIsAuthenticated, selectUser } from "@/store/slices/authSlice";
import { cn } from "@/lib/utils";

export interface UserInfoProps {
  className?: string;
}

/**
 * UserInfo Component
 *
 * Displays authenticated user information (name, email, customUserId).
 * Follows MVVM pattern - view component only, no business logic.
 *
 * @example
 * ```tsx
 * <UserInfo />
 * <UserInfo className="bg-gray-100 p-4" />
 * ```
 */
export const UserInfo = React.forwardRef<HTMLDivElement, UserInfoProps>(
  ({ className }, ref) => {
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const user = useAppSelector(selectUser);

    if (!isAuthenticated || !user) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex flex-col gap-[0.3125rem] p-4 bg-gray-50 border border-gray-100 rounded-[0.3125rem]",
          "font-montserrat text-gray-900",
          className
        )}
      >
        <div className="flex items-center gap-[0.625rem]">
          <div className="flex items-center justify-center w-[2rem] h-[2rem] bg-brand-red text-white rounded-full font-semibold text-[0.875rem]">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex flex-col">
            <span className="text-[0.9375rem] font-semibold">{user.name}</span>
            <span className="text-[0.75rem] text-gray-500">{user.email}</span>
          </div>
        </div>
        {user.customUserId && (
          <div className="text-[0.75rem] text-gray-600 mt-[0.3125rem]">
            ID: {user.customUserId}
          </div>
        )}
      </div>
    );
  }
);

UserInfo.displayName = "UserInfo";
